import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

/**
 * Détecte si l'utilisateur utilise un navigateur intégré (in-app browser)
 * comme Instagram, Facebook, TikTok, LinkedIn, etc.
 */
function detectInAppBrowser() {
  if (typeof window === "undefined") return { isInApp: false, platform: null };

  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Détection des navigateurs intégrés courants
  const patterns = [
    { regex: /instagram/i, name: "Instagram" },
    { regex: /FBAN|FBAV/i, name: "Facebook" },
    { regex: /Twitter/i, name: "Twitter" },
    { regex: /LinkedIn/i, name: "LinkedIn" },
    { regex: /TikTok/i, name: "TikTok" },
    { regex: /Snapchat/i, name: "Snapchat" },
    { regex: /Line\//i, name: "Line" },
    { regex: /WhatsApp/i, name: "WhatsApp" },
    { regex: /Messenger/i, name: "Messenger" },
  ];

  for (const pattern of patterns) {
    if (pattern.regex.test(ua)) {
      return { isInApp: true, platform: pattern.name };
    }
  }

  return { isInApp: false, platform: null };
}

/**
 * Bannière qui s'affiche quand l'utilisateur est dans un navigateur intégré
 * et guide vers l'ouverture dans un navigateur principal (Safari, Chrome, etc.)
 */
export default function InAppBrowserBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [browser, setBrowser] = useState<{ isInApp: boolean; platform: string | null }>({
    isInApp: false,
    platform: null,
  });

  useEffect(() => {
    const detected = detectInAppBrowser();
    setBrowser(detected);

    // Vérifier si l'utilisateur a déjà fermé la bannière dans cette session
    const wasDismissed = sessionStorage.getItem("inAppBrowserBannerDismissed");
    if (wasDismissed === "true") {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("inAppBrowserBannerDismissed", "true");
  };

  // Ne rien afficher si pas dans un navigateur intégré ou si fermé
  if (!browser.isInApp || dismissed) {
    return null;
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] p-4 bg-gradient-to-b from-black/50 to-transparent pointer-events-none">
      <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-2xl pointer-events-auto animate-in slide-in-from-top duration-500">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg mb-1">
                ⚠️ Problème de connexion détecté
              </h3>
              <p className="text-sm text-white/95 mb-3">
                Vous utilisez le navigateur intégré de <strong>{browser.platform}</strong>.
                Pour vous connecter correctement, vous devez ouvrir ce site dans{" "}
                <strong>{isIOS ? "Safari" : isAndroid ? "Chrome" : "votre navigateur principal"}</strong>.
              </p>

              {!showInstructions ? (
                <Button
                  onClick={() => setShowInstructions(true)}
                  variant="outline"
                  size="sm"
                  className="bg-white text-orange-600 hover:bg-orange-50 border-white font-semibold"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Comment faire ?
                </Button>
              ) : (
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 mt-2 border border-white/20">
                  <p className="font-semibold mb-2 text-sm">📱 Instructions :</p>
                  {browser.platform === "Instagram" && (
                    <ol className="text-sm space-y-1 list-decimal list-inside">
                      <li>Appuyez sur les <strong>3 points</strong> en haut à droite</li>
                      <li>Sélectionnez <strong>"Ouvrir dans {isIOS ? "Safari" : "Chrome"}"</strong></li>
                      <li>Connectez-vous depuis le navigateur qui s'ouvre</li>
                    </ol>
                  )}
                  {browser.platform === "Facebook" && (
                    <ol className="text-sm space-y-1 list-decimal list-inside">
                      <li>Appuyez sur les <strong>3 points</strong> en haut à droite</li>
                      <li>Sélectionnez <strong>"Ouvrir dans le navigateur"</strong></li>
                      <li>Connectez-vous depuis le navigateur qui s'ouvre</li>
                    </ol>
                  )}
                  {!["Instagram", "Facebook"].includes(browser.platform || "") && (
                    <ol className="text-sm space-y-1 list-decimal list-inside">
                      <li>Cherchez l'option <strong>"Ouvrir dans le navigateur"</strong> dans le menu</li>
                      <li>Ou copiez l'URL et collez-la dans {isIOS ? "Safari" : "Chrome"}</li>
                      <li>Connectez-vous depuis le navigateur qui s'ouvre</li>
                    </ol>
                  )}
                  <p className="text-xs mt-2 text-white/80">
                    💡 Les navigateurs intégrés bloquent les cookies nécessaires à la connexion.
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
